import { useEffect, useRef } from 'react';
import cn from 'classnames';
import { GroupBase, MultiValueProps, SelectComponentsConfig, components as ReactSelectComponents } from 'react-select';
import { useMultiValueContainerContext } from '../context/MultiValueContainerContext';
import { ISelectOption } from '../interfaces';
import Tooltip from '@creditflowresearch/uilib/Tooltip';

import styles from '../select.module.scss';

interface ISelectOptionWithValidation extends ISelectOption {
  isInvalid?: boolean,
}

interface IProps<SOption extends ISelectOptionWithValidation, IsMulti extends boolean, Group extends GroupBase<SOption>>
{
  dataTest: string,
  components?: SelectComponentsConfig<SOption, IsMulti, Group>,
  multiValueClassName?: string,
  isUseResponsiveTagsCount?: boolean,
  invalidValuesTooltipTitle?: string,
  ownProps: MultiValueProps<SOption, IsMulti, Group>,
}

const MultiValue = <
  SOption extends ISelectOptionWithValidation,
  IsMulti extends boolean,
  Group extends GroupBase<SOption>,
>({
  dataTest,
  components = {},
  multiValueClassName,
  invalidValuesTooltipTitle = 'Invalid option(s):',
  isUseResponsiveTagsCount,
  ownProps: {
    children,
    ...otherProps
  },
}: IProps<SOption, IsMulti, Group>) => {
  const dataTestAttr = `${ dataTest }_selected`;
  const { data: option, index, selectProps } = otherProps;
  const itemRef = useRef<HTMLDivElement>(null);

  const {
    counterRef,
    displayCount,
    registerTagSize,
  } = useMultiValueContainerContext();

  useEffect(() => {
    if (!isUseResponsiveTagsCount || !itemRef.current) {
      return undefined;
    }
    const unregister = registerTagSize(index, itemRef.current.offsetWidth);
    return () => unregister(index);
  }, [index, isUseResponsiveTagsCount, registerTagSize]);

  const isVisible = index < displayCount;
  const isShowCounter = index === displayCount && isUseResponsiveTagsCount;

  const optionData = option as SOption;

  const selectedValues = Array.isArray(selectProps.value) ? selectProps.value as SOption[] : [];
  const hiddenValues = selectedValues.slice(displayCount);
  const invalidValues = hiddenValues.filter((val) => val.isInvalid);
  const hasInvalidHidden = !!invalidValues.length;

  const tooltipContent = isShowCounter && (
    <>
      { hasInvalidHidden && <span>{ invalidValuesTooltipTitle } </span> }
      { (hasInvalidHidden ? invalidValues : hiddenValues).map(({ label }, idx) => (
        // eslint-disable-next-line react/no-array-index-key
        <div key={ `${ label }-${ idx }` }>{ label }</div>
      )) }
    </>
  );

  const renderMultiValue = () => {
    if (components?.MultiValue) {
      const { MultiValue: MultiValueComponent } = components;
      return <MultiValueComponent { ...otherProps }>{ children }</MultiValueComponent>;
    }
    return (
      <ReactSelectComponents.MultiValue { ...otherProps }>
        { children }
      </ReactSelectComponents.MultiValue>
    );
  };

  return (
    <>
      <div
        ref={ isUseResponsiveTagsCount ? itemRef : undefined }
        data-test={ dataTestAttr }
        className={ cn(
          multiValueClassName,
          {
            [styles.multiValue]: isUseResponsiveTagsCount,
            [styles.multiValueIsNotValid]: optionData.isInvalid,
            [styles.multiValueIsVisible]: isUseResponsiveTagsCount && isVisible,
          },
        ) }
      >
        { renderMultiValue() }
      </div>
      { isShowCounter && (
        <Tooltip
          content={ tooltipContent }
          className={ styles.multiValueWithValidationTooltip }
          { ...hasInvalidHidden && { type: 'error' } }
        >
          <div
            ref={ counterRef }
            className={ cn(
              styles.hiddenValuesCount,
              { [styles.countWithError]: hasInvalidHidden },
            ) }
          >
            +{ selectedValues.length - displayCount }
          </div>
        </Tooltip>
      ) }
    </>
  );
};

export default MultiValue;
